// The actions that need a reason from a person, all in one dialog shape: Turn off (with an
// optional "until" for a manual pause), Turn on (who fixed the account), Stop retries today,
// Skip this week, Hand to dev for a fix, Release. Each is audited with the reason and who.

import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '@mui/material';
import { notThere, release, requestFix, skipWeek, stopToday, turnOff, turnOn } from './api';
import { jobName } from './deskModel';

const TOO_OLD = 'This needs the newer Browser Desk API, which is not deployed yet.';

// kind -> what the dialog says and does. `ctx` is { row, jobType, deskBusy }.
const KINDS = {
    off: (r) => ({
        title: `Turn off ${r.id}?`,
        sub: 'The desk will not open this account (scheduled or on request) until someone turns it back on. Never automatic.',
        label: 'Why', cta: 'Turn off', danger: true, until: true,
        placeholder: 'e.g. vendor outage until Friday; password being reset by district IT',
        go: (reason, until) => turnOff(r.id, reason, until || undefined),
        done: `${r.id} turned off`,
    }),
    on: (r) => {
        const byDesk = r.disabled && r.disabled.page_class && r.disabled.page_class !== 'manual';
        return {
            title: `Turn on ${r.id}?`,
            sub: r.disabled ? `Turned off by ${r.disabled.by || 'the desk'}: ${r.disabled.reason || r.disabled.page_class}` : '',
            warn: byDesk ? 'The desk turned this login off. Turning it on before the account is fixed fails again and counts against its sign-ins.' : null,
            label: byDesk ? 'Who fixed the account, and how?' : 'Why it can run again',
            placeholder: byDesk ? 'e.g. district IT reset the password 10-15 (ticket 4411)' : 'e.g. vendor outage over',
            cta: 'Turn on', go: (reason) => turnOn(r.id, reason), done: `${r.id} turned on`,
        };
    },
    stop: (r) => ({
        title: `Stop retries today for ${r.id}`,
        sub: 'No more scheduled runs or retries today. Run now by a person still works, and it runs again on its next run day.',
        label: 'Why', placeholder: 'e.g. usage-minutes capture fails until the module fix ships',
        cta: 'Stop retries today', go: (reason) => stopToday(r.id, reason), done: `${r.id}: no more retries today`,
    }),
    skip: (r, ctx) => ({
        title: `Skip ${jobName(r, ctx.jobType)} this week`,
        sub: 'Marks it done for this week, so the catch-up stops; it runs next on its own day.',
        label: 'Why', placeholder: 'e.g. the vendor published nothing new this week',
        cta: 'Skip this week', go: (reason) => skipWeek(r.id, ctx.jobType, reason), done: `${jobName(r, ctx.jobType)} skipped this week`,
    }),
    fix: (r, ctx) => ({
        title: `Hand ${r.id} to dev for a fix`,
        sub: 'The prod desk stops running it; the dev desk takes over until someone releases it after the fix ships.',
        warn: ctx.deskBusy ? 'Its desk is running it right now: a hand-off is refused mid-run. Try again when the run ends.' : null,
        label: 'What\'s broken', placeholder: 'e.g. HMH Standards export page changed; module raises AttributeError',
        cta: 'Hand to dev', blocked: ctx.deskBusy, go: (reason) => requestFix(r.id, reason), done: `${r.id} handed to the dev desk`,
    }),
    release: (r) => ({
        title: `Release ${r.id} back to prod`,
        sub: r.fixing ? `In fix since ${(r.fixing.at || '').slice(0, 10)} by ${r.fixing.by}: ${r.fixing.reason}` : '',
        label: 'What was fixed', placeholder: 'e.g. fix merged in PR 6240 and deployed',
        cta: 'Release', go: (reason) => release(r.id, reason), done: `${r.id} released: prod runs it again`,
    }),
};

// target: { kind, row, jobType?, deskBusy? } or null.
const ReasonDialog = ({ target, onClose, onDone }) => {
    const [reason, setReason] = useState('');
    const [until, setUntil] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    useEffect(() => { setReason(''); setUntil(''); setError(null); }, [target]);
    const spec = target ? KINDS[target.kind](target.row, target) : null;
    const submit = async () => {
        setBusy(true); setError(null);
        try {
            const res = await spec.go(reason.trim(), until);
            onDone(res?.message || spec.done);
        } catch (e) {
            setError(notThere(e) ? TOO_OLD : e.message);
        } finally {
            setBusy(false);
        }
    };
    return (
        <Dialog open={Boolean(target)} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle className="d-font">{spec?.title}</DialogTitle>
            <DialogContent>
                {spec?.sub && <Typography className="d-font" sx={{ fontSize: 13, mb: 2 }}>{spec.sub}</Typography>}
                {spec?.warn && <Alert severity="warning" sx={{ mb: 2 }}>{spec.warn}</Alert>}
                {spec?.until && (
                    <TextField label="Until (optional, a manual pause only)" type="date" size="small" sx={{ mb: 2 }}
                        value={until} onChange={(e) => setUntil(e.target.value)} InputLabelProps={{ shrink: true }}
                        helperText="A reminder for people: the desk never turns it back on by itself." />
                )}
                <TextField label={`${spec?.label || 'Why'} (required)`} fullWidth size="small" autoFocus multiline minRows={2}
                    value={reason} onChange={(e) => setReason(e.target.value)} placeholder={spec?.placeholder} />
                {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
            </DialogContent>
            <DialogActions sx={{ '& .MuiButton-root': { textTransform: 'none' } }}>
                <Button onClick={onClose}>Cancel</Button>
                <Box sx={{ display: 'inline-flex' }}>
                    <Button variant="contained" color={spec?.danger ? 'error' : 'primary'}
                        disabled={!reason.trim() || busy || Boolean(spec?.blocked)} onClick={submit}>
                        {spec?.cta}
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
};

export default ReasonDialog;
